import { useLocalStorage } from "usehooks-ts";
import { FilterSettings, defaultFilterSettings } from "../models/schedule/filter-settings";
import { useCallback } from "react";
import { useLocation } from "react-router";
import { MyPagesRoutes } from "../types/routes";

type SetFilterSettingsFieldName = keyof Omit<FilterSettings, "filter"> | undefined;

export function useFilterSettings(): [
	FilterSettings,
	<T extends SetFilterSettingsFieldName>(
		fieldName: T,
		newValue: T extends keyof FilterSettings ? FilterSettings[T] : FilterSettings,
	) => void,
] {
	const { pathname } = useLocation();
	const [showApplicationShifts, setShowApplicationShifts] = useLocalStorage(
		"show_application_shifts",
		defaultFilterSettings.showApplicationShifts,
	);
	const [showDeclinedShifts, setShowDeclinedShifts] = useLocalStorage(
		"show_declined_shifts",
		defaultFilterSettings.showDeclinedShifts,
	);
	const [disabledSystems, setDisabledSystems] = useLocalStorage(
		"disabled_systems",
		defaultFilterSettings.disabledSystems.join(","),
	);
	const [showReadNews, setShowReadNews] = useLocalStorage(
		"show_read_news",
		defaultFilterSettings.showReadNews,
	);
	const [showESigningDocuments, setShowESigningDocuments] = useLocalStorage(
		"show_e_signing_documents",
		defaultFilterSettings.showESigningDocuments,
	);
	const [showDocuments, setShowDocuments] = useLocalStorage(
		"show_documents",
		defaultFilterSettings.showDocuments,
	);
	const [showUnreadMessages, setUnreadMessages] = useLocalStorage(
		"show_unread_messages",
		defaultFilterSettings.showUnreadMessages,
	);
	const [showCalendarMonth, setShowCalendarMonth] = useLocalStorage(
		"show_calendar_month",
		defaultFilterSettings.showCalendarMonth,
	);

	const setFilterSettings = useCallback(
		<T extends SetFilterSettingsFieldName>(
			fieldName: T,
			newValue: T extends keyof FilterSettings ? FilterSettings[T] : FilterSettings,
		) => {
			switch (fieldName) {
				case "showDocuments": {
					setShowDocuments(newValue as FilterSettings["showDocuments"]);
					break;
				}
				case "showESigningDocuments": {
					setShowESigningDocuments(newValue as FilterSettings["showESigningDocuments"]);
					break;
				}
				case "showApplicationShifts": {
					setShowApplicationShifts(newValue as FilterSettings["showApplicationShifts"]);
					break;
				}
				case "showDeclinedShifts": {
					setShowDeclinedShifts(newValue as FilterSettings["showDeclinedShifts"]);
					break;
				}
				case "disabledSystems": {
					setDisabledSystems((newValue as FilterSettings["disabledSystems"])?.join(","));
					break;
				}
				case "showReadNews": {
					setShowReadNews(newValue as FilterSettings["showReadNews"]);
					break;
				}
				case "showUnreadMessages": {
					setUnreadMessages(newValue as FilterSettings["showUnreadMessages"]);
					break;
				}
				case "showCalendarMonth": {
					setShowCalendarMonth(newValue as FilterSettings["showCalendarMonth"]);
					break;
				}
				case undefined: {
					const newFilterSettings = newValue as FilterSettings;
					const path = pathname.replace("/", "");
					if (path === MyPagesRoutes.Schedule) {
						setShowApplicationShifts(newFilterSettings.showApplicationShifts);
						setShowDeclinedShifts(newFilterSettings.showDeclinedShifts);
					}
					if (path === MyPagesRoutes.Messages) {
						setUnreadMessages(newFilterSettings.showUnreadMessages);
					}
					if (path === MyPagesRoutes.News) {
						setShowReadNews(newFilterSettings.showReadNews);
					}
					if (path === MyPagesRoutes.Documents) {
						setShowESigningDocuments(newFilterSettings.showESigningDocuments);
						setShowDocuments(newFilterSettings.showDocuments);
					}
					setDisabledSystems(newFilterSettings.disabledSystems.join(","));
				}
			}
		},
		[
			setDisabledSystems,
			setShowApplicationShifts,
			setShowDeclinedShifts,
			setShowReadNews,
			pathname,
			setShowESigningDocuments,
			setShowDocuments,
			setUnreadMessages,
			setShowCalendarMonth,
		],
	);

	const filterSettings: FilterSettings = new FilterSettings({
		showApplicationShifts,
		showDeclinedShifts,
		disabledSystems,
		showReadNews,
		showESigningDocuments,
		showDocuments,
		showUnreadMessages,
		showCalendarMonth,
	});

	return [filterSettings, setFilterSettings];
}
